// Sanitizer for Composer Block body HTML.
//
// Whitelists the formatting set exposed by BlockBodyEditor:
//   bold, italic, underline, bulleted list, links (with target/rel),
//   text alignment, and indent (margin-left in px) on paragraphs.
//
// Mirrors the inline-style hook pattern used by sanitizeCorpusHtml so we keep
// alignment + indent but strip every other inline style.
import DOMPurify from "isomorphic-dompurify";

const STYLE_WHITELIST = /(^|;)\s*(text-align|margin-left)\s*:\s*[^;]+/gi;

const filterInlineStyle = (raw: string): string => {
  const kept: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = STYLE_WHITELIST.exec(raw)) !== null) {
    kept.push(m[0].replace(/^;\s*/, "").trim());
  }
  return kept.join("; ");
};

let hookInstalled = false;
const ensureStyleHook = () => {
  if (hookInstalled) return;
  hookInstalled = true;
  DOMPurify.addHook("uponSanitizeAttribute", (_node, data) => {
    if (data.attrName !== "style") return;
    const filtered = filterInlineStyle(String(data.attrValue ?? ""));
    if (!filtered) {
      data.keepAttr = false;
      return;
    }
    data.attrValue = filtered;
  });
};

export const sanitizeBlockHtml = (html: string): string => {
  ensureStyleHook();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "em", "b", "i", "u",
      "ul", "ol", "li", "a",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "style"],
    ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:|\/|#)/i,
  });
};

/** Strip all HTML for a plain-text fallback (e.g. text/plain drag payload). */
export const blockHtmlToPlainText = (html: string): string => {
  if (!html) return "";
  // Treat block-level boundaries as newlines, then strip tags.
  const withBreaks = html
    .replace(/<\/(p|li|ul|ol|div)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n");
  const stripped = withBreaks.replace(/<[^>]+>/g, "");
  return stripped
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

/** True when the string already contains HTML tags. Used to detect legacy
 * plain-text block bodies that need to be wrapped into <p> tags on load. */
export const looksLikeHtml = (s: string): boolean => /<[a-z][\s\S]*>/i.test(s);

/** Wrap legacy plain-text body into paragraphs so the rich editor renders it. */
export const plainTextToBlockHtml = (text: string): string => {
  if (!text) return "";
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return text
    .split(/\n{2,}/)
    .map((para) => {
      const lines = para.split(/\n/).map(escape).join("<br />");
      return `<p>${lines || "<br />"}</p>`;
    })
    .join("");
};
